import { makeRequest } from "./client";
import type { Notice } from "./types";
import type {
  PlannerAddedEventRequest,
  PlannerDraft,
  PlannerEventPayload,
  PlannerEventWhenAndWhere,
  SavedPlanner,
} from "../../domain/planners/planner";

const plannersPath = (orgId: string) => `/v1/org/${encodeURIComponent(orgId)}/planners`;
const plannerPath = (orgId: string, plannerId: string) =>
  `${plannersPath(orgId)}/${encodeURIComponent(plannerId)}`;

const auth = (token: string) => ({ Authorization: `Bearer ${token}` });

/** Planners: only reachable when the Organization has the `planner` flag on. */
export const plannersApi = {
  listPlanners: async (orgId: string, token: string): Promise<{ planners: SavedPlanner[] }> =>
    makeRequest<{ planners: SavedPlanner[] }>(plannersPath(orgId), {
      method: "GET",
      headers: auth(token),
    }),

  getPlanner: async (
    orgId: string,
    plannerId: string,
    token: string,
  ): Promise<{ planner: SavedPlanner }> =>
    makeRequest<{ planner: SavedPlanner }>(plannerPath(orgId, plannerId), {
      method: "GET",
      headers: auth(token),
    }),

  /** Saves a new Draft Planner. */
  createPlanner: async (
    orgId: string,
    draft: PlannerDraft,
    token: string,
  ): Promise<{ planner: SavedPlanner }> =>
    makeRequest<{ planner: SavedPlanner }>(plannersPath(orgId), {
      method: "POST",
      headers: auth(token),
      body: JSON.stringify(draft),
    }),

  /** Replaces a Draft Planner's details and rows. */
  updatePlanner: async (
    orgId: string,
    plannerId: string,
    draft: PlannerDraft,
    token: string,
  ): Promise<{ planner: SavedPlanner }> =>
    makeRequest<{ planner: SavedPlanner }>(plannerPath(orgId, plannerId), {
      method: "PUT",
      headers: auth(token),
      body: JSON.stringify(draft),
    }),

  /** Renames a sent Planner. Its title is the only thing that can change once it's sent. */
  renamePlanner: async (
    orgId: string,
    plannerId: string,
    title: string,
    token: string,
  ): Promise<{ planner: SavedPlanner }> =>
    makeRequest<{ planner: SavedPlanner }>(plannerPath(orgId, plannerId), {
      method: "PUT",
      headers: auth(token),
      body: JSON.stringify({ title }),
    }),

  /**
   * Sends a Draft Planner: each row's Event becomes an ordinary Event. Nothing is
   * created if any row fails; the error's response body then has `rowProblems`.
   */
  sendPlanner: async (
    orgId: string,
    plannerId: string,
    events: PlannerEventPayload[],
    token: string,
  ): Promise<{ planner: SavedPlanner }> =>
    makeRequest<{ planner: SavedPlanner }>(`${plannerPath(orgId, plannerId)}/send`, {
      method: "POST",
      headers: auth(token),
      body: JSON.stringify({ events }),
    }),

  /**
   * Adds an Event to a sent Planner, with the Planner's audience. Adding the same
   * row again returns the Event already added. If the Event has problems, the
   * error's response body has `rowProblems`.
   */
  addPlannerEvent: async (
    orgId: string,
    plannerId: string,
    request: PlannerAddedEventRequest,
    token: string,
  ): Promise<{ notice: Notice | null; planner: SavedPlanner }> =>
    makeRequest<{ notice: Notice | null; planner: SavedPlanner }>(
      `${plannerPath(orgId, plannerId)}/events`,
      {
        method: "POST",
        headers: auth(token),
        body: JSON.stringify(request),
      },
    ),

  /**
   * Moves a sent Planner's Event to a new date, time or location; its Event
   * reminders move with it. `letParentsKnow` also sends parents a notification.
   * The same change again does nothing (`changed: false`).
   */
  changePlannerEvent: async (
    orgId: string,
    plannerId: string,
    noticeId: string,
    request: PlannerEventWhenAndWhere & { letParentsKnow: boolean },
    token: string,
  ): Promise<{ notice: Notice; changed: boolean }> =>
    makeRequest<{ notice: Notice; changed: boolean }>(
      `${plannerPath(orgId, plannerId)}/events/${encodeURIComponent(noticeId)}`,
      {
        method: "PATCH",
        headers: auth(token),
        body: JSON.stringify(request),
      },
    ),

  /**
   * Deletes a Draft Planner, or a sent one with no Events left. One with Events
   * is deleted with them: `notices.delete(eventId, …, { scope: "planner" })`.
   */
  deletePlanner: async (
    orgId: string,
    plannerId: string,
    token: string,
  ): Promise<{ message: string }> =>
    makeRequest<{ message: string }>(plannerPath(orgId, plannerId), {
      method: "DELETE",
      headers: auth(token),
    }),
};
